<script setup lang="ts">
    import type { Schemas } from '#shopware';

    const { appliedPromotionCodes, addPromotionCode, removeItem } = useCart();
    const { getErrorsCodes } = useCartNotification();
    const { hfpPushError } = useNotificationHelpers();
    // Blocking checkout is central; this only disables its own controls.
    const { isWritingToShopware } = useShopwareWriteState();

    const { t } = useI18n();

    const runPromotionMutation = async (mutate: () => Promise<unknown>) => {
        await mutate();

        getErrorsCodes()?.forEach((element) => {
            hfpPushError(t(`errors.${element.messageKey}`, { ...element }));
        });
    };

    const addPromotionCodeHandler = async (code: string) => {
        await runPromotionMutation(() => addPromotionCode(code));

        promoCode.value = '';
    };

    const removeItemHandler = async (appliedPromotionCode: Schemas['LineItem']) => {
        await runPromotionMutation(() => removeItem(appliedPromotionCode));
    };

    const showPromotionCodes = computed(() => appliedPromotionCodes.value.length > 0);

    const promoCode = ref('');
</script>

<template>
    <div>
        <div class="promotion-wrapper">
            <div>
                <div class="promotion-cart input-wrapper">
                    <input
                        v-model="promoCode"
                        type="text"
                        name="promoCode"
                        class="promotion-input"
                        :placeholder="t('form.promoCodePlaceholder')"
                    />

                    <PrimaryButton
                        product-button
                        data-testid="add-promo-button"
                        :button-text="t('checkout.promoCode.redeem')"
                        :disabled="isWritingToShopware"
                        @click="addPromotionCodeHandler(promoCode)"
                    />
                </div>
            </div>
            <div
                v-if="showPromotionCodes"
                class="code-wrapper"
            >
                <div class="label">{{ t('checkout.promoCode.label') }}:</div>
                <ul
                    class="code-list"
                    data-testid="promotion-code-list"
                >
                    <li
                        v-for="appliedPromotionCode in appliedPromotionCodes"
                        :key="appliedPromotionCode.id"
                        class="remove-code"
                    >
                        <span>{{ appliedPromotionCode.label }}</span>
                        <span>{{ appliedPromotionCode.referencedId }}</span>

                        <button
                            type="button"
                            class="delete"
                            :disabled="isWritingToShopware"
                            data-testid="product-remove-button"
                            @click="removeItemHandler(appliedPromotionCode)"
                        >
                            <span class="aria-hidden">{{ t('checkout.promoCode.ariaRemove') }}</span>

                            <img
                                src="~/assets/icons/trash.svg"
                                :alt="t('checkout.promoCode.remove')"
                                aria-hidden="true"
                            />
                        </button>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/cart/promotion-code.scss';
</style>
